/**
 * tm/pages/mobileKit.tsx — 휴대폰 화면(/m) 공용 입력 조각 (2026-10-12 MobileWork.tsx 에서 옮김 · 찾기 · 일정 화면도 함께 씀)
 *  2026-10-12 밤 토스 스타일(검토 5인 취합): 회색 바탕 + 흰 묶음 · 파랑 하나(#3182F6) · 빨강은 「지남」만 · 아이콘은 연한 파랑 바탕 + 파랑 그림 ·
 *   본문 17 · 보조 15(이하 금지) · 보조 글자는 #4E5968 보다 진하게(햇빛) · 누르는 곳 48 이상 · 주된 버튼은 아래 하나 56
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { G, Group, Icon, ListHeader, ListRow, clamp } from '../portal-ui/tds';
import { addDays, diffDays, kstToday, md } from '../flow/flowLogic';

/** 토스 스타일 색 — 화면마다 따로 고르지 않는다 */
export const TOSS = { blue: '#3182F6', blueDk: '#1B64DA', blueLt: '#E8F3FF', red: '#F04452', bg: '#F2F4F6', line: '#E5E8EB', text: '#191F28', sub: '#4E5968', faint: '#8B95A1' } as const;
/** /m 의 강조색 — 예전 남색 자리를 토스 파랑으로(이 파일을 쓰는 화면만 · 포털 · PC 는 그대로) */
export const NAVY = TOSS.blue;
/** 목록 한 줄 규격(2026-10-28 지도사 「폰트 · 레이아웃 구성 제각각이면 보기 힘듦」) — 업무 · 상담 · 통화 · 메모 · 기본 정보가 같은 크기 · 같은 순서
 *  제목(무엇) 17 굵게 · 둘째 줄(언제 · 누구) 15 회색 · 오른쪽 상태 15 굵게 · 본문 16 · 빈 안내 16 회색 · 줄 높이 64 */
export const ROW = {
  minHeight: 64,
  title: { display: 'block', fontSize: 17, fontWeight: 700, color: TOSS.text } as CSSProperties,
  sub: { display: 'block', fontSize: 15, color: TOSS.sub, fontVariantNumeric: 'tabular-nums' } as CSSProperties,
  state: { flexShrink: 0, fontSize: 15, fontWeight: 700 } as CSSProperties,
  body: { fontSize: 16, color: G.g800, lineHeight: 1.6 } as CSSProperties,
  empty: { padding: '12px 0', fontSize: 16, color: TOSS.sub } as CSSProperties,
};
export const input: CSSProperties = { width: '100%', height: 52, padding: '0 14px', borderRadius: 14, border: `1px solid ${G.g300}`, fontSize: 17, color: G.g900, background: '#FAFBFC', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
export const label: CSSProperties = { fontSize: 15, fontWeight: 700, color: TOSS.sub, margin: '20px 0 8px' };

/** 연한 파랑 바탕 + 파랑 그림 아이콘(토스식 · 색은 하나) */
export function Tint({ icon, size = 40 }: { icon: string; size?: number }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: Math.round(size * 0.32), background: TOSS.blueLt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={icon} size={Math.round(size * 0.52)} color={TOSS.blue} sw={2} /></span>;
}
/** 흰 묶음 — 회색 바탕 위 · 햇빛 아래에서도 경계가 보이게 옅은 테두리 */
export function Card({ children, pad = '8px 0', m = '0 0 12px' }: { children: ReactNode; pad?: string; m?: string }) {
  return <section style={{ background: '#fff', borderRadius: 20, border: `1px solid ${TOSS.line}`, padding: pad, margin: m, overflow: 'hidden' }}>{children}</section>;
}
/** 묶음 제목 줄 — 제목 17 · 오른쪽 글자 버튼(48 높이) */
export function CardHead({ title, count, right }: { title: string; count?: number; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 20px 2px', minHeight: 40 }}>
      <span style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{title}{count !== undefined && <span style={{ color: TOSS.faint, fontWeight: 600 }}> {count}</span>}</span>
      {right}
    </div>
  );
}
/** 토스식 버튼 — fill 파랑 · weak 연한 파랑 · gray */
export function Button({ children, kind = 'fill', h = 56, onClick, ariaLabel, muted }: { children: ReactNode; kind?: 'fill' | 'weak' | 'gray'; h?: number; w?: number; full?: boolean; onClick?: () => void; ariaLabel?: string; muted?: boolean }) {
  const look: Record<string, CSSProperties> = { fill: { background: TOSS.blue, color: '#fff' }, weak: { background: TOSS.blueLt, color: TOSS.blueDk }, gray: { background: TOSS.bg, color: TOSS.sub } };
  return (
    <button type="button" onClick={onClick} aria-label={ariaLabel} className="tds-btn"
      style={{ ...look[kind], ...(muted ? { opacity: 0.55 } : {}), height: h, width: '100%', padding: '0 14px', fontSize: h >= 56 ? 17 : 16, fontWeight: 700, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box', maxWidth: '100%', overflow: 'hidden' }}>
      {typeof children === 'string' ? <span style={muted ? clamp(2) : clamp(1)}>{children}</span> : children}
    </button>
  );
}
/** 아래에서 올라오는 고르기 판(확인 창 대신) */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)' }} />
      <div role="dialog" aria-label={title} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxWidth: 640, maxHeight: '88vh', overflowY: 'auto', overscrollBehavior: 'contain', margin: '0 auto', background: '#fff', borderRadius: '24px 24px 0 0', padding: '20px 20px calc(16px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: TOSS.text, marginBottom: 8 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

export function Field({ title, need, children, note }: { title: string; need?: boolean; children: ReactNode; note?: string }) {
  return (
    <div>
      <div style={label}>{title}{need && <span style={{ color: '#D22030' }}> *</span>}</div>
      {children}
      {note && <div style={{ fontSize: 15, color: G.g700, marginTop: 6, lineHeight: 1.5, wordBreak: 'keep-all' }}>{note}</div>}
    </div>
  );
}

/** cols — 같은 폭 칸으로 한 줄(오와 열 · 3+1 로 꺾이지 않게) */
export function Chips({ items, value, onChange, multi, cols }: { items: { k: string; t: string }[]; value: string[]; onChange: (v: string[]) => void; multi?: boolean; cols?: number }) {
  return (
    <div style={cols ? { display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 8 } : { display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {items.map(it => {
        const on = value.includes(it.k);
        return (
          <button key={it.k} type="button" className="tds-btn" onClick={() => onChange(multi ? (on ? value.filter(x => x !== it.k) : [...value, it.k]) : [it.k])}
            style={{ ...clamp(1), maxWidth: '100%', minHeight: 48, padding: cols ? '0 4px' : '0 16px', borderRadius: cols ? 12 : 22, border: `${on ? 2 : 1}px solid ${on ? TOSS.blue : G.g300}`, background: on ? TOSS.blueLt : '#fff', color: on ? TOSS.blueDk : TOSS.sub, fontSize: 16, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit' }} aria-pressed={on}>{on ? '✓ ' : ''}{it.t}</button>
        );
      })}
    </div>
  );
}

export function Check({ on, onChange, children }: { on: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, minHeight: 44, padding: '10px 0', boxSizing: 'border-box', fontSize: 16, lineHeight: 1.45, color: G.g800, cursor: 'pointer', marginRight: 18, wordBreak: 'keep-all' }}>
      <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} style={{ width: 22, height: 22, margin: '1px 0 0', flexShrink: 0 }} /><span>{children}</span>
    </label>
  );
}

/** 고르기 — 검색 + 목록(최근 순 6개) · 고른 뒤엔 한 줄 + 「바꾸기」 */
export function Picker<T extends { id: string; name: string; updated_at?: string; created_at?: string }>({ items, value, onChange, sub, placeholder, recent, extra, optional }: {
  items: T[]; value: string | null; onChange: (id: string | null) => void; sub: (x: T) => string; placeholder: string; recent: ByRecent; /* T 는 items 로만 정한다(recent 가 T 추론에 끼면 T 가 {id,name} 으로 줄어 빌드가 깨졌다 · 2026-10-12) */ extra?: ReactNode | ((q: string) => ReactNode);
  optional?: string;   // 선택 칸 — 처음엔 한 줄로 접어 둔다(필수 칸을 화면 밖으로 밀지 않게)
}) {
  const [q, setQ] = useState('');
  const [openOpt, setOpenOpt] = useState(false);
  const sel = items.find(x => x.id === value);
  if (!sel && optional && !openOpt) {
    return (
      <button type="button" className="tds-btn" onClick={() => setOpenOpt(true)}
        style={{ ...input, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: G.g700, cursor: 'pointer', textAlign: 'left' }}>
        {optional}<Icon name="r" size={18} color={G.g500} sw={2} />
      </button>
    );
  }
  if (sel) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 56, padding: '6px 6px 6px 14px', borderRadius: 14, background: TOSS.blueLt, border: `2px solid ${TOSS.blue}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: G.g900, ...clamp(1) }}>{sel.name}</div>
          {sub(sel) && <div style={{ fontSize: 14, color: G.g700, ...clamp(1) }}>{sub(sel)}</div>}
        </div>
        <button type="button" className="tds-btn" onClick={() => onChange(null)} style={{ minHeight: 44, padding: '0 14px', border: 'none', background: 'none', color: NAVY, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>바꾸기</button>
      </div>
    );
  }
  const k = q.trim();
  const list = (k ? items.filter(x => x.name.includes(k) || sub(x).includes(k)) : items.slice().sort(recent)).slice(0, k ? 12 : 3);   // 최근은 3개(아래 칸을 밀지 않게 · 검토)
  return (
    <div>
      <label className="tds-in" style={{ ...input, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="search" size={20} color={G.g600} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} aria-label={placeholder} style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 17, background: 'transparent', fontFamily: 'inherit' }} />
      </label>
      <div style={{ marginTop: 6, borderRadius: 14, border: `1px solid ${G.g200}`, overflow: 'hidden', background: '#fff' }}>
        {!k && <div style={{ padding: '8px 14px 0', fontSize: 15, color: TOSS.sub }}>최근</div>}
        {list.map(x => <ListRow key={x.id} title={x.name} sub={sub(x)} arrow onClick={() => { onChange(x.id); setQ(''); }} clip pad="10px 14px" />)}
        {list.length === 0 && <div style={{ padding: 14, fontSize: 15, color: G.g600 }}>찾는 이름이 없어요</div>}
        {typeof extra === 'function' ? extra(k) : extra}
      </div>
    </div>
  );
}

/** 저장 버튼 줄 — 화면 아래에 붙어 있어 키보드 · 긴 화면에서도 보인다 */
export function SaveBar({ children, side = 24 }: { children: ReactNode; side?: number }) {
  return <div style={{ position: 'sticky', bottom: 0, zIndex: 5, background: '#fff', margin: `22px -${side}px 0`, padding: `12px ${side}px calc(12px + env(safe-area-inset-bottom, 0px))`, borderTop: `1px solid ${G.g200}` }}>{children}</div>;
}

/** 빠진 칸 안내 — 버튼 글자 그대로 3초 */
export function guard(missing: string, busy: boolean, toast: (m: string) => void): boolean {
  if (busy) return false;
  if (missing) { toast(missing); return false; }
  return true;
}

/** 날짜 — 자주 쓰는 날 칩 + 달력(다른 날) · 고른 날은 「10/15(목)」로 */
export function DatePick({ value, onChange }: { value: string; onChange: (d: string) => void }) {
  const t = kstToday();
  // 오늘 · 내일 · 모레 + 다른 날(달력) — 왜 그 날인지 셈하지 않게(검토 5인)
  const opts = [{ k: t, t: '오늘' }, { k: addDays(t, 1), t: '내일' }, { k: addDays(t, 2), t: '모레' }];
  const other = !opts.some(o => o.k === value);
  return (
    <div>
      <Chips cols={3} items={opts} value={[value]} onChange={v => onChange(v[0])} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <span style={{ flex: 1, fontSize: 17, fontWeight: 700, color: value ? G.g900 : TOSS.sub }}>{value ? md(value) : '날짜를 골라 주세요'}{value && <span style={{ fontWeight: 400, color: TOSS.sub, fontSize: 15 }}> · {diffDays(t, value) === 0 ? '오늘' : `${diffDays(t, value)}일 뒤`}</span>}</span>
        <label style={{ position: 'relative', minHeight: 48, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 16px', borderRadius: 12, border: 'none', background: other && value ? TOSS.blueLt : TOSS.bg, color: other && value ? TOSS.blueDk : TOSS.sub, fontSize: 16, fontWeight: 700 }}>
          <Icon name="cal" size={18} color={other && value ? TOSS.blue : TOSS.sub} />다른 날
          <input type="date" lang="ko" value={value} min={t} onChange={e => onChange(e.target.value)} aria-label="다른 날 고르기"
            style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
        </label>
      </div>
    </div>
  );
}
export type ByRecent = (a: { updated_at?: string; created_at?: string }, b: { updated_at?: string; created_at?: string }) => number;

/** 저장 뒤 이어서 할 일 */
export function Next({ items }: { items: { t: string; on: () => void }[] }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, []);   // 저장하면 「이어서」로 바로 내려감
  return (
    <div ref={ref}><Group m="20px 0 0" pad="4px 0">
      <ListHeader title="이어서" />
      {items.map(i => <ListRow key={i.t} title={i.t} arrow onClick={i.on} />)}
    </Group></div>
  );
}
